import numeral from "numeral";
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Badge, Button, IconButton, Tooltip } from '@mui/material';
import classNames from "classnames";
import { getTime } from "date-fns";
import { DomEvent } from 'leaflet';
import { AccountContext } from '../context/account';
import { ExploreContext } from '../context/explore';
import { MapDataContext } from '../context/map-data';
import { MeshResponseDataValueProps } from '../core/global';
import { storeItem } from "../core/storage";
import { interpolateValues, isNumber } from "../helpers/core";
import { trackEvent } from "../helpers/tracking";
import useLoggedIn from "../hooks/useLoggedIn";
import styles from "../styles/components/popup-content.module.scss";
import { getProjectByLocation } from "./explore";
import Loader from "./loader";
import { MapPreview } from "./map-preview";
import { LatLonBanner } from './mouse-coordinates';
import { PanToLocationButton } from './pan-to-location-button';
import { BoundsState } from "./rectangle";

const showSelection = false

export const PopUpContent = () => {
  
  const {
    setShowPurchaseData,
    setSelectedPoint,
    projects
  } = useContext(AccountContext);

  const {
    updateMeshData,
    meshDataGeoJson,
    getFeatureLocation,
    dataSourceModel,
    currentLayer,
    setCurrentLayer,
    dataSources,
    setScaleValues,
    showAverages,
    currentSelectionParams,
  } = useContext(MapDataContext);

  const {
    exploreLocationParams,
    setExploreLocationParams,
    isPurchasedPoint,
    secondaryDataSchema,
  } = useContext(ExploreContext);

  const ref = useRef<HTMLDivElement | null>(null);

  const userIsLoggedIn = useLoggedIn();

  useEffect(() => {
    if (ref.current) {
      DomEvent.disableClickPropagation(ref.current);
    }
    return () => {
      ref.current = null;
    }
  }, [ref]);

  const {
    lat,
    lon,
    cenLat,
    cenLon,
    sqm
  } = useMemo(() => {
    return getFeatureLocation()
  }, [JSON.stringify(meshDataGeoJson)])

  const content: (MeshResponseDataValueProps | null)[] = useMemo(() => {
    if (meshDataGeoJson 
      && meshDataGeoJson.features 
      && Array.isArray(meshDataGeoJson.features)
      && meshDataGeoJson.features.length > 0
      && dataSourceModel
      && dataSources
    ) {
      const {properties} = meshDataGeoJson.features[0]
      const {model, provider, geography, version, key} = dataSourceModel
      const layers = (((Object.prototype.hasOwnProperty.call(dataSources, key)) ? dataSources[key].layers : null) || []).filter( l => l.valueKey !== 'model-grid')

      return layers.map(layer => {
        const {units, title, shortName, valueKey} = layer
        if (valueKey) {
          const hasValue = (!showAverages && properties && Object.prototype.hasOwnProperty.call(properties, valueKey))
          || (showAverages && Array.isArray(meshDataGeoJson.features) && meshDataGeoJson.features.some( f => {
            const props = f.properties || {}
            return Object.prototype.hasOwnProperty.call(props, valueKey)
          }))
          if (!hasValue) {
            return null
          }
          let value = properties ? properties[valueKey] : ''
          let name = shortName
          if (showAverages && Array.isArray(meshDataGeoJson.features) && meshDataGeoJson.features.length > 1) {
            const values = meshDataGeoJson.features.map(f => {
              const props = f.properties || {}
              return props[valueKey]
            }).filter( v => typeof v === 'number' && !isNaN(v))
            if (values.length === 4) {
              const points = meshDataGeoJson.features.map(r => {
                if (!r.properties || !r.properties.centroid) {
                  return null
                }
                const [centroid_lat, centroid_lon] = r.properties.centroid.split(' ').map(Number);
                return {
                  lat: centroid_lat,
                  lon: centroid_lon,
                  val: r.properties?.[valueKey] ?? null,
                }
              }).filter( p => p !== null) as {lat: number, lon: number, val: number}[];
              value = interpolateValues(lat, lon, points)
            } else {
              value = 'N/A'
            }
            name += ' (interpolated)'
          }
          
            return {
              name,
              valueKey,
              units,
              value,
              model, 
              provider, 
              version,
              geography,
              layerName: title
            }
        }
        return null
      }).filter( i => i !== null)
    }
    return []
  }, [JSON.stringify(meshDataGeoJson), showAverages, JSON.stringify(dataSources), dataSourceModel])

  const setCoordsToExporeData = useCallback(() => {
    if (
      dataSourceModel &&
      projects !== null &&
      isNumber(cenLat) && 
      isNumber(cenLon)
    ) {
      if (exploreLocationParams) {
        const {lat: exploreLat, lon: exploreLon} = exploreLocationParams;
        if (cenLat === exploreLat && cenLon === exploreLon) {
          return;
        }
      }
      const {model, provider, geography, version} = dataSourceModel;
      const found = getProjectByLocation(dataSourceModel, projects, cenLat, cenLon);
      const isFullDataPoint = found !== null;
      // setExploreCoords([cenLat, cenLon])

      if (isFullDataPoint) {
        const startDate = getTime(new Date(found.start_date))
        const endDate = getTime(new Date(found.end_date))
        const [,orderId,filename] = (found.path || '').split("/");
        setExploreLocationParams({
          model,
          provider,
          geography,
          version,
          lat: cenLat,
          lon: cenLon,
          startDate, 
          endDate, 
          orderId,
          filename,
        })
      } else {
        setExploreLocationParams({
          model,
          provider,
          geography,
          version,
          lat: cenLat,
          lon: cenLon,
          lengthYears: 1
        })
      }
      
      
      
    }
  }, [JSON.stringify(projects), dataSourceModel, cenLat, cenLon, exploreLocationParams]);

  const currentDataSource = useMemo(() => {
    if (dataSourceModel && dataSources) {
      const {key} = dataSourceModel
      return (Object.prototype.hasOwnProperty.call(dataSources, key)) ? dataSources[key] : null
    }
    return null
  }, [dataSourceModel, JSON.stringify(dataSources)])

  const isOnDemand = useMemo(() => {
    if (currentDataSource) {
      const {model, regular} = currentDataSource
      // !!(currentDataSource  && currentDataSource.regular === true)
      return model.toLowerCase().includes('ondemand') && regular
    }
    return false
  }, [currentDataSource])

  const {overlayPolygon, overlayPolygonProperties} = useMemo(() => {
    if (secondaryDataSchema && secondaryDataSchema?.description?.wkb_geometry) {
      const overlayPolygon = secondaryDataSchema.description.wkb_geometry || null
      const overlayPolygonProperties = secondaryDataSchema.description.name ? {
        name: secondaryDataSchema.description.name
      } : null
      return {
        overlayPolygon,
        overlayPolygonProperties
      }
    }
    return {
      overlayPolygon: null,
      overlayPolygonProperties: null
    }
  }, [secondaryDataSchema])

  const f = useCallback((a: any) => {
    return numeral(a).format("0.00")
  }, [])
  
  const calculateBoundsAreaSqM = useCallback((bounds: BoundsState | null) => {
    if (
      !bounds ||
      bounds.latMin == null ||
      bounds.latMax == null ||
      bounds.lonMin == null ||
      bounds.lonMax == null
    ) {
      return null
    }
  
    const earthRadiusMeters = 6_371_000
  
    const minLatitudeRad = bounds.latMin * Math.PI / 180
    const maxLatitudeRad = bounds.latMax * Math.PI / 180
    const longitudeSpanRad = Math.abs(bounds.lonMax - bounds.lonMin) * Math.PI / 180
  
    const areaSqM =
      earthRadiusMeters ** 2 *
      longitudeSpanRad *
      Math.abs(Math.sin(maxLatitudeRad) - Math.sin(minLatitudeRad))
  
    return numeral(areaSqM/1_000_000).format("0,0.00")
  }, [])

  const hasConfig = useMemo(() => {
    // check for config in data source
    if (currentDataSource) {
      const {options} = currentDataSource
      return !!options
    }
    return false
  }, [currentDataSource])

  if (!meshDataGeoJson) {
    if (isPurchasedPoint) {
      // if (currentProjectPoint === null) {
        return (
          <div className={classNames(styles.contentWrapper, styles.contentWrapperWithLoader)} style={{ height: "200px" }}>
            <Loader center small />
          </div>
        )
      // }
    }
    return (
      <div className={styles.contentWrapper}>
        <p>Click an area on the map to load a location detail.</p>
      </div>
    )
  }

  if (isNumber(lat) && isNumber(lon)) {
    return (
      <div className={classNames(styles.contentWrapper)}>
        {
        (lat === cenLat && lon === cenLon) ?
          <LatLonBanner lat={lat} lon={lon} enableCopy />
        : 
          <>
            <LatLonBanner lat={lat} lon={lon} title={'Selected location'} enableCopy />
            <LatLonBanner lat={cenLat} lon={cenLon} title={'Data centroid'} enableCopy />
          </>
        }
        {(sqm && sqm > 0) ? 
          <p><strong>Approximate cell resolution:</strong> {numeral(sqm).format("0,0.0")} m</p>
          : <></>
        }
        {currentSelectionParams && showSelection && 
          <div className={styles.currentParamsWrapper}>
            <h5>Selection parameters:</h5>
            {currentSelectionParams.innerBounds && <p>Outer bounds: <strong>Lat: </strong>{f(currentSelectionParams.innerBounds.latMin)} to {f(currentSelectionParams.innerBounds.latMax)} <strong>Lon:</strong> {f(currentSelectionParams.innerBounds.lonMin)} to {f(currentSelectionParams.innerBounds.lonMax)}</p>}
            {currentSelectionParams.innerBounds && <p>Approx. area: {calculateBoundsAreaSqM(currentSelectionParams.innerBounds)} km</p>}
            {currentSelectionParams.outerBounds && <p>Outer bounds: <strong>Lat: </strong>{f(currentSelectionParams.outerBounds.latMin)} to {f(currentSelectionParams.outerBounds.latMax)} <strong>Lon:</strong> {f(currentSelectionParams.outerBounds.lonMin)} to {f(currentSelectionParams.outerBounds.lonMax)}</p>}
            {currentSelectionParams.innerBounds && <p>Approx. area: {calculateBoundsAreaSqM(currentSelectionParams.outerBounds)} km</p>}
          </div>
        }
        {content.length > 0 ?
          <>
            <table>
              <tbody>
                {content.map(item => {
                    if (item) {
                      const {
                        name,
                        units,
                        value,
                        valueKey,
                        layerName,
                      } = item
                      return (
                        <tr 
                          key={valueKey} 
                          className={classNames({
                            [styles.isClickable]: typeof layerName !== 'undefined' && exploreLocationParams === null,
                            // [styles.highlight]: valueKey === currentLayer?.valueKey && exploreLocationParams === null
                        })}
                          onClick={() => {
                            if (dataSources && dataSourceModel) {
                              const {key} = dataSourceModel
                              const layers = (((Object.prototype.hasOwnProperty.call(dataSources, key)) ? dataSources[key].layers : null) || []).filter( l => l.valueKey !== 'model-grid')
                              const found = layers.find(l => l.valueKey === valueKey)
                              if (found && exploreLocationParams === null) {
                                setCurrentLayer(found)
                                setScaleValues(null)
                                const {title} = found
                                trackEvent("layer-change", {layer: title})
                              }  
                            }
                          }}
                        >
                          <th>
                            {currentLayer && valueKey === currentLayer?.valueKey && exploreLocationParams === null && 
                              <Badge 
                                className={styles.badge}
                                variant="dot"
                                color="success" 
                                anchorOrigin={{
                                  vertical: "top",
                                  horizontal: 'left',
                                }} />
                            }
                            {name}
                          </th>
                          {value === 'N/A' && <td className={styles.value}>N/A</td>}
                          {value !== 'N/A' && <td className={styles.value}>{numeral(value).format("0,0.00")} {units}</td>}
                        </tr>
                      )  
                    }
                    return null
                  })
                }
              </tbody>
            </table>
          </>
        :
          <div className={styles.warning}>
            <FmdBadIcon />
            <p><strong>We don't have any data for the given model and location.</strong></p>
          </div>
        }
        <div ref={ref} className={styles.row}>
          <Button
            id="explore-button"
            className={styles.button}
            variant="outlined"
            size="small"
            startIcon={<QueryStatsIcon />}
            disabled={content.length === 0 || isOnDemand }
            onClick={(e: React.SyntheticEvent) => {
              e.preventDefault()
              setCoordsToExporeData()
            }}
          >
            Visualise
          </Button>
          <Button
            // disabled={!userIsLoggedIn}
            id="add-to-basket-button-explore"
            // className={styles.button}
            variant="contained"
            color="success"
            size="small"
            startIcon={<ShoppingCartIcon sx={{color: '#ffffff'}} />}
            disabled={content.length === 0}
            onClick={async (e: React.SyntheticEvent) => {
              e.preventDefault()
              setShowPurchaseData(true)
              const {model, provider, geography, version} = dataSourceModel || {};
              trackEvent("add-to-basket-intent", {lat: cenLat, lon: cenLon, model, provider, geography, version})
              setExploreLocationParams(null);
            }}
          >
            {hasConfig? 'Configure & buy' : 'Add to basket'}
          </Button>
          <Tooltip
            title={'Clear location'} 
            placement="bottom" 
            arrow
          >
            <IconButton 
              size='small'
              onClick={() => {
                setExploreLocationParams(null);
                updateMeshData({});
                storeItem('map-location', null)
              }
            }>
              <ClearIcon />
            </IconButton>
          </Tooltip>
          <PanToLocationButton 
            onClick={() => {
              if (isNumber(lat) && isNumber(lon)) {
                setSelectedPoint([lat, lon])
              }
            }}
            size='small' 
            lat={lat} 
            lon={lon} 
          />
        </div>
        {exploreLocationParams !== null && isNumber(cenLat) && isNumber(cenLon) ?
          <div className={styles.contentWrapper} style={{margin: "16px 0 0 0"}}>
            <MapPreview 
              lat={Number(cenLat)} 
              lon={Number(cenLon)} 
              overlayPolygon={overlayPolygon} 
              overlayPolygonProperties={overlayPolygonProperties}
            />
          </div>
          :
          null
        }
      </div>
    )

  }
  return null
}